import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { collection, doc, getDoc, getDocs, query, where } from "firebase/firestore";

import { useAuth } from "../context/AuthContext";
import { db } from "../firebase";
import "./WordDropLeaderboard.css";

function etParts(now = new Date()) {
  const bag = {};
  new Intl.DateTimeFormat("en-US", {
    timeZone: "America/New_York",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    weekday: "short",
    hourCycle: "h23",
  }).formatToParts(now).forEach((part) => {
    bag[part.type] = part.value;
  });
  return bag;
}

function weekStartKey(now = new Date()) {
  const parts = etParts(now);
  const utc = new Date(Date.UTC(Number(parts.year), Number(parts.month) - 1, Number(parts.day)));
  const daysSinceMonday = (utc.getUTCDay() + 6) % 7;
  utc.setUTCDate(utc.getUTCDate() - daysSinceMonday);
  const month = String(utc.getUTCMonth() + 1).padStart(2, "0");
  const day = String(utc.getUTCDate()).padStart(2, "0");
  return `${utc.getUTCFullYear()}-${month}-${day}`;
}

function shiftDateKey(iso, days) {
  const [year, month, day] = String(iso).split("-").map(Number);
  const utc = new Date(Date.UTC(year, month - 1, day));
  utc.setUTCDate(utc.getUTCDate() + days);
  const nextMonth = String(utc.getUTCMonth() + 1).padStart(2, "0");
  const nextDay = String(utc.getUTCDate()).padStart(2, "0");
  return `${utc.getUTCFullYear()}-${nextMonth}-${nextDay}`;
}

function asNumber(value) {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
}

/** Live week window: Monday 9:00am ET through now (ET calendar date keys). */
function currentWeekBounds(now = new Date()) {
  const parts = etParts(now);
  const weekday = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].indexOf(parts.weekday);
  const hour = Number(parts.hour);
  let start = weekStartKey(now);
  if (weekday === 1 && hour < 9) start = shiftDateKey(start, -7);
  const today = `${parts.year}-${parts.month}-${parts.day}`;
  return { start, end: today, today };
}

function daysUntilMondayReset(now = new Date()) {
  const parts = etParts(now);
  const weekday = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].indexOf(parts.weekday);
  const hour = Number(parts.hour);
  if (weekday === 1 && hour < 9) return 0;
  const daysSinceMonday = (weekday + 6) % 7;
  const untilNext = daysSinceMonday === 0 ? 7 : 7 - daysSinceMonday;
  return untilNext;
}

function rankLiveScores(scoreDocs, { today, creatorId }) {
  const totals = new Map();
  scoreDocs.forEach((data) => {
    const userId = data.userId;
    if (!userId) return;
    const prev = totals.get(userId) || {
      userId,
      points: 0,
      daysPlayed: 0,
      guessSum: 0,
      guessN: 0,
      todayGuesses: 0,
      note: "",
      displayName: "",
      avgGuesses: null,
    };
    prev.points += asNumber(data.points);
    prev.daysPlayed += 1;
    const guesses = asNumber(data.guessCount);
    if (guesses > 0) {
      prev.guessSum += guesses;
      prev.guessN += 1;
    }
    if (data.date === today && userId === creatorId && data.solved === true) {
      prev.todayGuesses = guesses;
    }
    totals.set(userId, prev);
  });
  return [...totals.values()]
    .map((row) => ({
      userId: row.userId,
      points: row.points,
      daysPlayed: row.daysPlayed,
      todayGuesses: row.todayGuesses,
      note: row.note,
      displayName: row.displayName,
      avgGuesses: row.guessN ? row.guessSum / row.guessN : null,
    }))
    .sort((a, b) => b.points - a.points || String(a.userId).localeCompare(String(b.userId)))
    .map((row, index) => ({ ...row, rank: index + 1 }));
}

function firstName(displayName) {
  const name = String(displayName || "").trim();
  if (!name) return "Player";
  return name.split(/\s+/)[0];
}

function shortName(displayName) {
  const parts = String(displayName || "").trim().split(/\s+/).filter(Boolean);
  if (parts.length < 2) return parts[0] || "Player";
  return `${parts[0]} ${parts[parts.length - 1][0].replace(".", "")}.`;
}

function initials(displayName) {
  const parts = String(displayName || "").trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "•";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
}

function normalizeRow(row) {
  return {
    userId: row.userId,
    rank: asNumber(row.rank),
    points: asNumber(row.points),
    daysPlayed: asNumber(row.daysPlayed),
    avgGuesses: row.avgGuesses == null || row.avgGuesses === "" ? null : asNumber(row.avgGuesses),
    todayGuesses: asNumber(row.todayGuesses),
    note: row.note || "",
    displayName: row.displayName || "",
  };
}

function neighbourhood(rows, viewerId, creatorId) {
  const sorted = [...rows].filter((row) => row.rank > 0).sort((a, b) => a.rank - b.rank);
  const picked = new Map();
  const add = (row) => {
    if (row?.userId) picked.set(row.rank, row);
  };
  sorted.filter((row) => row.rank <= 3).forEach(add);
  const creator = sorted.find((row) => row.userId === creatorId);
  if (creator && creator.rank > 3) add(creator);
  const viewer = sorted.find((row) => row.userId === viewerId);
  if (viewer) {
    add(sorted.find((row) => row.rank === viewer.rank - 1));
    add(viewer);
    add(sorted.find((row) => row.rank === viewer.rank + 1));
  }
  const list = [...picked.values()].sort((a, b) => a.rank - b.rank);
  const rendered = [];
  list.forEach((row, index) => {
    if (index > 0 && row.rank > list[index - 1].rank + 1) {
      rendered.push({ kind: "gap", key: `gap-${row.rank}` });
    }
    rendered.push({ kind: "row", key: row.userId, row });
  });
  return rendered;
}

function sumBoards(boards) {
  const totals = new Map();
  boards.forEach((board) => {
    (board.top || []).forEach((row) => {
      const prev = totals.get(row.userId) || {
        userId: row.userId,
        points: 0,
        daysPlayed: 0,
        displayName: row.displayName || "",
        note: "",
        todayGuesses: 0,
        avgGuesses: null,
      };
      prev.points += asNumber(row.points);
      prev.daysPlayed += asNumber(row.daysPlayed);
      if (!prev.displayName && row.displayName) prev.displayName = row.displayName;
      totals.set(row.userId, prev);
    });
  });
  return [...totals.values()]
    .sort((a, b) => b.points - a.points || String(a.userId).localeCompare(String(b.userId)))
    .map((row, index) => ({ ...row, rank: index + 1 }));
}

function daysMeta(row, range) {
  if (!row.daysPlayed) return "";
  if (range === "all") return `${row.daysPlayed} days`;
  const base = `${row.daysPlayed} of 7 days`;
  if (row.avgGuesses) return `${base} · avg ${Number(row.avgGuesses).toFixed(1)}`;
  return base;
}

function metaFor(row, { isCreator, isYou, above, range }) {
  if (isCreator && range === "week" && row.todayGuesses) {
    return `Creator · got today's in ${row.todayGuesses}`;
  }
  if (isCreator) return "Creator";
  if (isYou && range === "week" && row.note) return row.note;
  if (isYou && above && above.points > row.points) {
    return `${above.points - row.points} points behind ${firstName(above.displayName)}`;
  }
  return daysMeta(row, range);
}

export default function WordDropLeaderboard() {
  const { currentUser } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const creatorId = (searchParams.get("creator") || "").trim();
  const grid = Number(searchParams.get("grid")) === 6 ? 6 : 5;
  const [range, setRange] = useState("week");
  const [creator, setCreator] = useState(null);
  const [weekly, setWeekly] = useState([]);
  const [liveWeek, setLiveWeek] = useState([]);
  const [names, setNames] = useState({});
  const [prize, setPrize] = useState({ weekly: "", allTime: "" });
  const [error, setError] = useState("");
  const weekBounds = useMemo(() => currentWeekBounds(), []);

  useEffect(() => {
    let alive = true;
    getDoc(doc(db, "config", "wordDropPrizes"))
      .then((snap) => {
        if (!alive || !snap.exists()) return;
        const data = snap.data() || {};
        setPrize({
          weekly: data.weekly || "",
          allTime: data.allTime || "",
        });
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    let alive = true;
    if (!creatorId) {
      setCreator(null);
      setWeekly([]);
      setLiveWeek([]);
      return undefined;
    }
    setError("");
    setWeekly([]);
    setLiveWeek([]);
    (async () => {
      try {
        const [profile, boards, scores] = await Promise.all([
          getDoc(doc(db, "users", creatorId)),
          getDocs(query(
            collection(db, "wordDrop_weeklyBoards"),
            where("creatorId", "==", creatorId)
          )),
          getDocs(query(
            collection(db, "wordDrop_scores"),
            where("creatorId", "==", creatorId),
            where("grid", "==", grid),
            where("date", ">=", weekBounds.start),
            where("date", "<=", weekBounds.end)
          )),
        ]);
        if (!alive) return;
        setCreator({
          id: creatorId,
          displayName: profile.exists() ? (profile.data()?.displayName || "Creator") : "Creator",
        });
        setWeekly(boards.docs
          .map((snap) => ({ id: snap.id, ...snap.data() }))
          .filter((board) => Number(board.grid) === grid));
        setLiveWeek(rankLiveScores(
          scores.docs.map((snap) => snap.data() || {}),
          { today: weekBounds.today, creatorId }
        ).map(normalizeRow));
      } catch (err) {
        if (!alive) return;
        setWeekly([]);
        setLiveWeek([]);
        setError(err?.message || "This board couldn't be loaded.");
      }
    })();
    return () => {
      alive = false;
    };
  }, [creatorId, grid, weekBounds]);

  const rows = useMemo(() => {
    if (range === "week") return liveWeek;
    return sumBoards(weekly.map((board) => ({
      ...board,
      top: (board.top || []).map(normalizeRow),
    })));
  }, [range, liveWeek, weekly]);

  useEffect(() => {
    const missing = [...new Set(rows.map((row) => row.userId).filter((id) => id && !Object.prototype.hasOwnProperty.call(names, id)))];
    if (!missing.length) return undefined;
    let alive = true;
    (async () => {
      const loaded = {};
      await Promise.all(missing.map(async (id) => {
        const snap = await getDoc(doc(db, "users", id));
        loaded[id] = snap.exists() ? (snap.data()?.displayName || "") : "";
      }));
      if (alive) setNames((prev) => ({ ...prev, ...loaded }));
    })();
    return () => {
      alive = false;
    };
  }, [rows, names]);

  const namedRows = rows.map((row) => ({
    ...row,
    displayName: shortName(names[row.userId] || row.displayName || "Player"),
  }));
  const visible = neighbourhood(namedRows, currentUser?.uid || "", creatorId);
  const resetDays = daysUntilMondayReset();
  const callout = range === "week" ? prize.weekly : prize.allTime;
  const leagueName = firstName(creator?.displayName);

  function selectGrid(next) {
    const params = new URLSearchParams(searchParams);
    params.set("grid", String(next));
    if (creatorId) params.set("creator", creatorId);
    setSearchParams(params, { replace: true });
  }

  return (
    <div className="wdl-page">
      <header className="wdl-header">
        <Link className="wdl-back" to={`/games?creator=${encodeURIComponent(creatorId)}`}>Back</Link>
        <div className="wdl-kicker">WORD DROP · BOARDS</div>
        <div className="wdl-header-spacer" />
      </header>

      <div className="wdl-segs" role="tablist" aria-label="Board">
        {[5, 6].map((size) => (
          <button
            key={size}
            type="button"
            role="tab"
            className={`wdl-tab${grid === size ? " active" : ""}`}
            aria-selected={grid === size}
            onClick={() => selectGrid(size)}
          >
            {size}-letter board
          </button>
        ))}
      </div>

      <div className="wdl-hero">
        <div className="wdl-hero-avatar" aria-hidden="true">{initials(creator?.displayName)}</div>
        <h1 className="wdl-title">{leagueName}'s {grid}-letter league</h1>
        <div className="wdl-sub">
          {range === "week"
            ? `Resets Monday 9:00am ET · ${resetDays === 0 ? "today" : `${resetDays} ${resetDays === 1 ? "day" : "days"} left`}`
            : "All-time points on this board"}
        </div>
      </div>

      <div className="wdl-segs range" role="tablist" aria-label="Range">
        <button type="button" role="tab" className={`wdl-tab${range === "week" ? " active" : ""}`} aria-selected={range === "week"} onClick={() => setRange("week")}>This week</button>
        <button type="button" role="tab" className={`wdl-tab${range === "all" ? " active" : ""}`} aria-selected={range === "all"} onClick={() => setRange("all")}>All time</button>
      </div>

      <div className="wdl-callout">{callout || "Prize details are coming."}</div>

      <div className="wdl-list">
        {error && <div className="wdl-empty">{error}</div>}
        {!error && visible.length === 0 && <div className="wdl-empty">No scores on this board yet.</div>}
        {visible.map((item) => {
          if (item.kind === "gap") return <div className="wdl-gap" key={item.key}>···</div>;
          const row = item.row;
          const isYou = Boolean(currentUser?.uid) && row.userId === currentUser.uid;
          const isCreator = row.userId === creatorId;
          const above = namedRows.find((entry) => entry.rank === row.rank - 1);
          const meta = metaFor(row, { isCreator, isYou, above, range });
          return (
            <div
              className={`wdl-row${isYou ? " you" : ""}${isCreator ? " creator" : ""}`}
              key={item.key}
            >
              <div className={`wdl-rank${row.rank <= 3 ? " top" : ""}`}>{row.rank}</div>
              <div className={`wdl-avatar${isCreator ? " creator" : ""}${isYou ? " you" : ""}`} aria-hidden="true">
                {initials(isYou ? (names[row.userId] || row.displayName) : row.displayName)}
              </div>
              <div className="wdl-main">
                <div className="wdl-name">{isYou ? "You" : row.displayName}</div>
                {meta && <div className="wdl-meta">{meta}</div>}
              </div>
              <div className="wdl-pts">{row.points}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
